import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { TrendingUp, Gift, Users, IndianRupee } from "lucide-react";

interface Coupon {
  id: string;
  title: string;
  coupon_code: string;
  minAmount: string;
  maxAmount: string;
  minSpend: string;
  expiryDate: string;
  status: "active" | "inactive" | "expired";
  activationStatus: string;
  isNew: number;
  termsAndConditions: string;
  maxUsage: number;
  imageUrl: string;
  forUsers: string;
  createdAt: string;
  updatedAt: string;

  assignedCount: number;
  popupSeenCount: number;
  scratchedCount: number;
  redeemedCount: number;
}

interface DashboardProps {
  coupons: Coupon[];
}

export function Dashboard({ coupons }: DashboardProps) {
  const activeCoupons = coupons.filter((c) => c.status === "active").length;

  const totalAssigned = coupons.reduce(
    (sum, c) => sum + Number(c.assignedCount || 0),
    0
  );

  const totalRedeemed = coupons.reduce(
    (sum, c) => sum + Number(c.redeemedCount || 0),
    0
  );

  const totalSeen = coupons.reduce(
    (sum, c) => sum + Number(c.popupSeenCount || 0),
    0
  );

  const redemptionRate =
    totalAssigned > 0 ? (totalRedeemed / totalAssigned) * 100 : 0;

  const recentCoupons = [...coupons]
    .sort((a, b) => Number(b.redeemedCount || 0) - Number(a.redeemedCount || 0))
    .slice(0, 5);

  return (
    <div className="coupon-dashboard-overview">
      <div className="coupon-dashboard-heading">
        <h2 className="text-gray-900">Overview</h2>
        <p className="text-gray-500 text-sm">
          Your coupon performance at a glance
        </p>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Coupons */}
        <Card className="coupon-stat-card">
          <CardHeader className="coupon-stat-header flex flex-row items-center justify-between">
            <CardTitle className="text-sm">Total Coupons</CardTitle>
            <span className="coupon-stat-icon" aria-hidden="true"><Gift className="size-4" /></span>
          </CardHeader>
          <CardContent className="coupon-stat-content">
            <div className="coupon-stat-value">{coupons.length}</div>
            <p className="coupon-stat-note">
              {activeCoupons} active coupons
            </p>
          </CardContent>
        </Card>

        {/* Assigned Users */}
        <Card className="coupon-stat-card">
          <CardHeader className="coupon-stat-header flex flex-row items-center justify-between">
            <CardTitle className="text-sm">Coupons Assigned</CardTitle>
            <span className="coupon-stat-icon" aria-hidden="true"><Users className="size-4" /></span>
          </CardHeader>
          <CardContent className="coupon-stat-content">
            <div className="coupon-stat-value">{totalAssigned.toLocaleString()}</div>
            <p className="coupon-stat-note">
              {totalSeen} opened popup
            </p>
          </CardContent>
        </Card>

        {/* Redeemed */}
        <Card className="coupon-stat-card">
          <CardHeader className="coupon-stat-header flex flex-row items-center justify-between">
            <CardTitle className="text-sm">Total Redeemed</CardTitle>
            <span className="coupon-stat-icon" aria-hidden="true"><IndianRupee className="size-4" /></span>
          </CardHeader>
          <CardContent className="coupon-stat-content">
            <div className="coupon-stat-value">{totalRedeemed}</div>
            <p className="coupon-stat-note">
              Successfully used coupons
            </p>
          </CardContent>
        </Card>

        {/* Redemption Rate */}
        <Card className="coupon-stat-card">
          <CardHeader className="coupon-stat-header flex flex-row items-center justify-between">
            <CardTitle className="text-sm">Redemption Rate</CardTitle>
            <span className="coupon-stat-icon" aria-hidden="true"><TrendingUp className="size-4" /></span>
          </CardHeader>
          <CardContent className="coupon-stat-content">
            <div className="coupon-stat-value">{Math.round(redemptionRate)}%</div>
            <p className="coupon-stat-note">Redeemed / Assigned</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performing Coupons */}
      <Card className="coupon-performance-card">
        <CardHeader className="coupon-performance-header">
          <CardTitle>Top Performing Coupons</CardTitle>
        </CardHeader>
        <CardContent className="coupon-performance-content">
          {recentCoupons.length === 0 ? (
            <div className="coupon-empty-state">No coupon performance data is available yet.</div>
          ) : (
          <div>
            {recentCoupons.map((coupon) => {
              const redemptionPercent =
                coupon.assignedCount > 0
                  ? Math.round(
                      (coupon.redeemedCount / coupon.assignedCount) * 100
                    )
                  : 0;

              return (
                <div
                  key={coupon.id}
                  className="coupon-performance-row flex items-center justify-between"
                >
                  <div className="flex items-center gap-4 flex-1">
                    {coupon.imageUrl && (
                      <img
                        src={coupon.imageUrl}
                        alt={coupon.title}
                        className="coupon-performance-image rounded-lg object-cover"
                      />
                    )}

                    <div className="flex-1">
                      <div className="flex mb-1 items-center gap-2">
                        <div className="text-gray-900">{coupon.title}</div>

                        {coupon.isNew === 1 && (
                          <Badge className="bg-green-600 hover:bg-green-700">
                            New
                          </Badge>
                        )}
                      </div>

                      <p className="text-sm text-gray-500">
                        ${coupon.minAmount} - ${coupon.maxAmount} · Min spend $
                        {coupon.minSpend}
                      </p>

                      <p className="text-sm text-gray-500">
                        Seen {coupon.popupSeenCount} · 
                       Redeemed{" "}
                        {coupon.redeemedCount}
                      </p>
                    </div>
                  </div>

                  <div className="coupon-performance-result text-right">
                    <p className="text-gray-900">
                      {coupon.assignedCount} assigned
                    </p>

                    <p className="text-sm text-gray-500">
                      {redemptionPercent}% redeemed
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
